import { useRef, type ReactElement, type ReactNode } from "react";
import { PIXEL_FACES, type SPACING } from "odyssey-shared";
import { Window, logical, useCentred } from "../ui/kit/index.ts";
import { ServerStrip, useServerNotice } from "./server-strip.tsx";

/**
 * The layout the account screens share (P1.9-T12, P1.4-T6; ADR-061 and its amendments, the design export a
 * visual reference only): a backdrop over the whole page holding the game's wordmark over one window, the
 * wordmark and the window each placed in the middle across and the two together in the middle down, on whole
 * logical pixels by measurement (`useCentred`), since the window can be a fraction of a logical pixel across.
 * The login screen (its log in and register forms and the name step) and character select draw their forms
 * in the window. The wordmark is "OdysseyTales" in the title face at its measured size, the design's size at
 * every screen size (the author's ruling of 2026-09-27, ADR-061's amendment "the kit and the entry forms match
 * the design"), alone: Aethervale and the epigraph are shown only if the author keeps them (ADR-064, open
 * question 5). At that size the log in and register forms fit the phone table's 445 by 230 (P1.9-T51); a form
 * that grows past the page scrolls inside the backdrop. Over the window, as wide as it and `stripGap` above it, the
 * server's state shows when there is something to say (ADR-078 decision 7, `ServerStrip`), and while it does the
 * wordmark is the design's small one, the title face at 24 (ADR-061's amendment "character select as redesigned").
 * A screen may set a part under the window, `belowGap` under it, such as character select's Log out everywhere;
 * the strip, the window and that part are placed together. A screen that is told the server's state another way than
 * the poll sets its own strip (`over`) in the poll's place, and may keep the small wordmark whether or not a strip
 * shows (`mark`), as the queue window does (P1.4-T164).
 */

/**
 * The game's wordmark, as the entry screens and the loading shell write it.
 */
export const WORDMARK = "OdysseyTales";

/**
 * A spacing step of the token table, checked at compile time.
 *
 * @param step One of `SPACING`.
 * @returns The step in logical pixels.
 */
function space(step: (typeof SPACING)[number]): number {
  return step;
}

/**
 * The layout's measurements in logical pixels: the wordmark at the title face's measured size and line, and the
 * design's small wordmark shown while a server notice shows; the window's width unless a screen gives its own; the
 * gap between the wordmark and the window, between the strip and the window, and between the window and the part
 * under it; and the backdrop's grid of one-pixel lines every 32.
 */
export const ENTRY_METRICS = {
  wordmark: { size: PIXEL_FACES.title.nativeSize, lineHeight: PIXEL_FACES.title.lineHeight },
  smallWordmark: { size: 24, lineHeight: 24 },
  window: 216,
  stackGap: space(12),
  stripGap: space(4),
  belowGap: space(6),
  backdropCell: 32,
} as const;

/**
 * An entry screen: the backdrop, the wordmark and a window titled for the form it holds.
 *
 * @param props The screen's `mode`, which the page and its tests read from `data-login-mode`; `layout`, a key
 *   that changes whenever the window's content may change size, so the placement is measured again; the
 *   window's `title`; its `width` in logical pixels, `ENTRY_METRICS.window` unless given; whether the server's
 *   `strip` shows over it, as it does unless the screen is the maintenance page; `over`, a strip of the screen's own
 *   shown in the poll's place, or null for none, which the screen gives as wide as the window; `mark`, "small" to
 *   keep the small wordmark with or without a strip; the window's `footer`, when it has one; the part `below` the
 *   window, when there is one; and the form it holds.
 * @returns The screen.
 */
export function EntryScreen({ mode, layout, title, width = ENTRY_METRICS.window, strip = true, over, mark, footer, below, children }: { readonly mode: string; readonly layout: string; readonly title: string; readonly width?: number; readonly strip?: boolean; readonly over?: ReactNode; readonly mark?: "small"; readonly footer?: ReactNode; readonly below?: ReactNode; readonly children: ReactNode }): ReactElement {
  const polled = useServerNotice() !== null && strip && over === undefined;
  const small = mark === "small" || polled || (over !== undefined && over !== null);
  const face = small ? ENTRY_METRICS.smallWordmark : ENTRY_METRICS.wordmark;
  const placed = `${layout}:${small ? "small" : "full"}`;
  const screen = useRef<HTMLDivElement>(null);
  const stack = useRef<HTMLDivElement>(null);
  const wordmark = useRef<HTMLHeadingElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const under = useRef<HTMLDivElement>(null);
  const stackAt = useCentred(screen, stack, { x: true, y: true }, placed);
  const wordmarkAt = useCentred(stack, wordmark, { x: true, y: false }, placed);
  const frameAt = useCentred(stack, frame, { x: true, y: false }, placed);
  const underAt = useCentred(frame, under, { x: true, y: false }, placed);
  const cell = logical(ENTRY_METRICS.backdropCell);
  return (
    <div ref={screen} className="ot-login" data-login="screen" data-login-mode={mode} style={{ backgroundSize: `${cell} ${cell}` }}>
      <div ref={stack} className="ot-login__stack" data-login="stack" style={{ left: logical(stackAt.left), top: logical(stackAt.top), gap: logical(ENTRY_METRICS.stackGap) }}>
        <h1 ref={wordmark} className="ot-login__wordmark" data-login="wordmark" data-wordmark={small ? "small" : "full"} style={{ left: logical(wordmarkAt.left), fontSize: logical(face.size), lineHeight: logical(face.lineHeight) }}>
          {WORDMARK}
        </h1>
        <div ref={frame} className="ot-login__frame" style={{ left: logical(frameAt.left), gap: logical(ENTRY_METRICS.stripGap) }}>
          {over === undefined ? strip ? <ServerStrip width={width} /> : null : over}
          <Window title={title} width={width} footer={footer}>
            {children}
          </Window>
          {below === undefined ? null : (
            <div ref={under} className="ot-login__below" data-login="below" style={{ left: logical(underAt.left), marginTop: logical(ENTRY_METRICS.belowGap - ENTRY_METRICS.stripGap) }}>
              {below}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
